import type { Key } from "react";

import type { CrmAccountRef } from "app/pages/sales-intelligence/domain/crm-integration/types/account";
import type { QuotaOperationState } from "app/pages/sales-intelligence/modules/quota/types";

import type { Integration } from "../../management/types";

import type { ExportFlowOrigin, ExportFlowPhase } from "./exportFlow";

export enum ContactEntryType {
    Email = "email",
    Mobile = "mobile",
    Direct = "direct",
}

export type RevealedContactDto = {
    id: string;
    phoneNumbers?: ContactEntryPhone[];
    emails?: string[];
    linkedInUrl: string;
    requester?: {
        name: string;
        email: string;
    };
    lastUpdated?: string;
    revealErrors?: {
        hasValidEmailsError?: boolean;
        hasDirectPhoneNumbersError?: boolean;
        hasMobilePhoneNumbersError?: boolean;
    };
};

export type ContactEntryPhone = {
    ext: string;
    number: string;
    type: string;
    numberType: string;
    internalType: ContactEntryType.Mobile | ContactEntryType.Direct;
    isDoNotCall?: boolean;
};

type BaseContactSearchEntry = {
    accuracyGrade?: string;
    companyName: string;
    favicon: string;
    companyId: string;
    country: string;
    countryCode: string;
    numericCountryCode: number;
    department: string;
    hasDirectPhoneNumbers: boolean;
    hasDirectPhoneNumbersSuppressed: boolean;
    hasMobilePhoneNumbers: boolean;
    hasMobilePhoneNumbersSuppressed: boolean;
    hasValidEmails: boolean;
    hasValidEmailsSuppressed: boolean;
    id: string;
    image: string;
    isRevealed: boolean;
    hasIssues?: boolean;
    linkedin: string;
    name: string;
    role: string;
    site: string;
    originalSite: string;
    seniority: string;
    title: string;

    // todo: (SI-7229) privateData field appears in Revealed Contact (RevealedContactSearchEntry) only. Remove it from here in SI-7229
    privateData?: Omit<RevealedContactDto, "revealErrors">;
};

export type RevealedContactSearchEntry = Omit<BaseContactSearchEntry, "isRevealed" | "privateData"> & {
    isRevealed: true;
    revealedInfo: RevealedContactDto;
    privateData: RevealedContactDto;
};

export type ContactSearchEntry = BaseContactSearchEntry | RevealedContactSearchEntry;
export type ContactSearchEntryWithKey = ContactSearchEntry & {
    key?: Key;
};

export type ContactsSearchTableFilters = {
    page: number;
    pageSize: number;
    searchText: string;
};

export type ContactsSearchSortBy = {
    orderBy?: string;
    asc?: boolean;
};

export type ContactsSearchResults = {
    excludedBySignalsCount?: number;
    totalCount?: number;
    revealedCount?: number;
    notRevealedCount?: number;
    rows: (ContactSearchEntry | RevealedContactSearchEntry)[];
};

export type RevealLimitReachedModalPayload = {
    isOpened: boolean;
    lastValidSelection: ContactSearchEntry[];
};

export type ContactIssueModalPayload = {
    contact: ContactSearchEntry;
} | null;

export type ContactIssuePayload = {
    IssueTypes: string[];
    issueDetails?: string;
    contactId?: string;
    companyId?: string;
    companyName?: string;
    companyDomain?: string;
    jobTitle?: string;
    directPhone?: string;
    mobilePhone?: string;
    email?: string;
};

export type ActionsPanelFilters = {
    includeOnlyRevealed: boolean;
    includeOnlyNotRevealed: boolean;
    includeOnlyExported: boolean;
    excludeExported: boolean;
    page?: number;
};

export type ContactsSearchStateType = {
    searchResults: ContactsSearchResults;
    searchResultsFetchError?: string;
    areSearchResultsFetching: boolean;
    isResultsTotalCountBeingFetched: boolean;
    tableFilters: ContactsSearchTableFilters;
    sortBy?: ContactsSearchSortBy;
    actionsPanelFilters: ActionsPanelFilters;
    selectedTableRows: ContactSearchEntryWithKey[];
    isRowsSelectionEnabled: boolean;
    quota: null | QuotaOperationState;
    cost: number;
    isQuotaBeingFetched: boolean;
    revealLimitReachedModal: RevealLimitReachedModalPayload;
    contactIssueModal: ContactIssueModalPayload;
    isMultipleRevealInProgress: boolean;
    isExportingToExcel: boolean;
    exportToExcelError?: string;
    exportVariant: ExportVariant | undefined;
    exportToCrm: {
        isFetchingPreview: boolean;
        contactsPreview: ContactExportPreview[];
        crm: Integration | undefined;
        as: CRMEntity | undefined;
    };
    isContactUsHookUsed: boolean;
    isActionPanelActivated: boolean;
    requestedContactsForExport: readonly ContactSearchEntry[];
    pendingRevealInfo: {
        revealedCount: number;
        unrevealedCount: number;
        requestedCount: number;
        isFullRevealSuccess: boolean;
    } | null;
    exportFlowPhase: ExportFlowPhase;
    exportFlowOrigin: ExportFlowOrigin | null;
};

export type CRMEntity = "contact" | "lead";

// Re-export shim: the canonical shape lives at the domain level.
export type Account = CrmAccountRef;

type ExportVariant = "excel" | "crm";

export type ExportDestination = "excel" | Integration;

export type ContactExportPreview = {
    contactId: string;
    firstName: string;
    lastName: string;
    jobTitle: string;
    accountName: string;
    accountDomain: string;
    accountId?: string;
    matchedCompanies: Account[];
    isExistAsLead?: boolean;
    searchedCompanyLabel?: string;
};

export type ContactDetails = {
    hasEmail?: boolean;
    hasMobilePhone?: boolean;
    hasDirectPhone?: boolean;
    hasEmailAndMobilePhone?: boolean;
};

export type ContactsQuotaActionPayload = Partial<Pick<ContactsSearchStateType, "cost">> & {
    quota: QuotaOperationState;
};
